import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Tag } from 'lucide-react';
import { SEO } from '../components/SEO';
import blogPostsData from '../content/blog.json';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  body: any;
  excerpt?: string;
  category?: string;
  featuredImage?: {
    url: string;
    title?: string;
  } | null;
  publishDate: string;
  featured?: boolean;
  createdAt: string;
  updatedAt: string;
}

export function BlogPage() {
  const [page, setPage] = useState(1);
  const postsPerPage = 9;

  // Convert imported data to typed array
  const allPosts = blogPostsData as BlogPost[];
  
  // Calculate pagination
  const totalPosts = allPosts.length;
  const totalPages = Math.ceil(totalPosts / postsPerPage);
  const startIndex = (page - 1) * postsPerPage;
  const endIndex = startIndex + postsPerPage;
  const posts = allPosts.slice(startIndex, endIndex);

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  function getImageUrl(url: string): string {
    // Ensure URL has protocol
    return url.startsWith('//') ? `https:${url}` : url;
  }

  return (
    <>
      <SEO 
        title="Blog / News | PlaylistPartner" 
        description="Stay updated with the latest news, features, and improvements from PlaylistPartner. Plus practical music marketing tips."
        type="website"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Playlist Partner Blog",
          "description": "Stay updated with the latest news, features, and improvements from Playlist Partner. Plus practical music marketing tips.",
          "url": window.location.href,
          "inLanguage": "en-US",
          "publisher": {
            "@type": "Organization",
            "name": "Playlist Partner",
            "logo": {
              "@type": "ImageObject",
              "url": "https://ftwlzdhkzqfuosdorgjt.supabase.co/storage/v1/object/public/playlistpartner%20public//playlist%20partner%20logo%20square.png",
              "width": "500",
              "height": "500"
            }
          }
        }}
      />
      
      <div className="bg-gradient-to-b from-green-50 to-white">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Playlist Partner Blog
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stay updated with the latest news, features, and improvements from Playlist Partner. Plus, coming soon: practical music marketing tips to boost your career.
            </p>
          </div>

          {posts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600">No blog posts available yet. Check back soon!</p>
            </div>
          )}

          {posts.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {posts.map((post) => (
                  <article 
                    key={post.id} 
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    {post.featuredImage?.url && (
                      <Link to={`/blog/${post.category?.toLowerCase() || 'news'}/${post.slug}`}>
                        <img 
                          src={getImageUrl(post.featuredImage.url)} 
                          alt={post.featuredImage.title || post.title}
                          className="w-full aspect-[3/2] object-cover"
                        />
                      </Link>
                    )}
                    
                    <div className="p-6">
                      <Link to={`/blog/${post.category?.toLowerCase() || 'news'}/${post.slug}`}>
                        <h2 className="text-xl font-semibold text-gray-900 mb-2 hover:text-green-600 transition-colors">
                          {post.title}
                        </h2>
                      </Link>
                      
                      {post.excerpt && (
                        <p className="text-gray-600 mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(post.publishDate)}</span>
                        </div>
                        
                        {post.category && (
                          <div className="flex items-center gap-1">
                            <Tag className="w-4 h-4" />
                            <span>{post.category}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`px-3 py-1 rounded-md ${
                          pageNum === page
                            ? 'bg-green-600 text-white'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
}